(() => {
  'use strict';
  if (customElements.get('me-learning')) return;
  class MELearning extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      this.phase = 0;
      this.frame = 0;
      this.last = null;
      this.visible = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = this.dataset.autoplay === 'true' && !this.motion.matches;
      this.hz = Number(this.dataset.hz) || 60;
      this.concept = 'impulse';
      this.choices = [...this.querySelectorAll('[data-choice]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.videos = [...this.querySelectorAll('video')];
      this.needles = [...this.querySelectorAll('[data-needle]')];
      this.sourceStages = [...this.querySelectorAll('[data-source-stage]')];
      this.button = this.querySelector('[data-play]');
      this.phaseInput = this.querySelector('[data-phase]');
      this.phaseOutput = this.querySelector('[data-phase-output]');
      this.chart = this.querySelector('[data-comparison-chart]');
      this.plot = this.chart?.querySelector('[data-chart-svg]');
      this.tick = this.tick.bind(this);
      this.querySelectorAll('[data-js-controls]').forEach(el => el.hidden = false);
      const on = (el, event, handler) => el?.addEventListener(event, handler, { signal: this.abort.signal });
      on(this, 'click', event => {
        const choice = event.target.closest('[data-choice]');
        if (choice && this.contains(choice)) this.select(choice);
        if (event.target.closest('[data-play]') === this.button && this.button) {
          this.playing = !this.playing;
          this.updatePlayback();
        }
      });
      on(this.querySelector('[data-frequency]'), 'input', event => {
        this.hz = Number(event.target.value);
        this.querySelector('[data-hz-output]').textContent = `${this.hz} Hz`;
      });
      on(this.phaseInput, 'input', event => {
        this.phase = Number(event.target.value) / 100;
        this.playing = false;
        this.updatePlayback();
        this.paint();
      });
      on(document, 'visibilitychange', () => this.updatePlayback());
      on(document, 'me:animation-colour', event => {
        if (this.colours?.some(c => c.handle === event.detail.handle)) this.setColour(event.detail.handle);
      });
      on(this.motion, 'change', event => {
        if (event.matches) this.playing = false;
        this.updatePlayback();
      });
      on(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const choice = this.choices.find(button => button.dataset.choice === event.detail.blockId);
        if (choice) this.select(choice);
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayback();
      }, { threshold: 0 });
      this.observer.observe(this);
      const colourData = this.querySelector('[data-colour-data]');
      if (colourData) {
        this.colours = JSON.parse(colourData.textContent);
        const control = this.querySelector('[data-colour-initial]');
        const initial = control?.dataset.colourInitial;
        this.setColour(initial || this.colours[0]?.handle);
        on(this.querySelector('[data-colour-select]'), 'change', event => {
          document.dispatchEvent(new CustomEvent('me:animation-colour', { detail: { handle: event.target.value } }));
        });
      }
      if (this.chart) {
        this.resizeObserver = new ResizeObserver(() => { this.drawPlot(); this.paint(); });
        this.resizeObserver.observe(this.chart);
        this.drawPlot();
      }
      if (this.choices.length) this.select(this.choices[0]);
      this.paint();
      this.updatePlayback();
    }
    select(choice) {
      this.choices.forEach(button => button.setAttribute('aria-pressed', String(button === choice)));
      this.panels.forEach(panel => panel.hidden = panel.dataset.panel !== choice.dataset.choice);
      this.concept = choice.dataset.concept || 'impulse';
      this.drawPlot();
      this.paint();
      this.updatePlayback();
    }
    setColour(handle) {
      const selected = this.colours?.find(c => c.handle === handle);
      if (!selected) return;
      this.colourRequest = (this.colourRequest || 0) + 1;
      const request = this.colourRequest;
      const select = this.querySelector('[data-colour-select]');
      if (select) select.value = handle;
      this.querySelectorAll('[data-colour-name]').forEach(el => el.textContent = selected.label);
      this.sourceStages.forEach(stage => stage.dataset.ready = 'false');
      const picture = new Image();
      picture.onload = () => {
        if (!this.isConnected || request !== this.colourRequest) return;
        this.sourceStages.forEach(stage => {
          stage.querySelector('[data-source-frame]').style.backgroundImage = `url("${selected.url}")`;
          stage.dataset.ready = 'true';
        });
        this.paint();
      };
      picture.onerror = () => {
        if (request !== this.colourRequest) return;
        this.sourceStages.forEach(stage => stage.dataset.ready = 'error');
      };
      picture.src = selected.url;
    }
    chartSeries() {
      const mode = this.dataset.kind === 'stroke' ? 'stroke' : this.concept;
      const c = this.chart.dataset;
      const travel = x => (1 - Math.cos(x * Math.PI * 2)) / 2;
      if (mode === 'stroke') return [
        { name: '2.8 mm', color: c.colorShort, dash: '6 4', value: x => 2.8 * travel(x) },
        { name: '3.5 mm', color: c.colorLong, value: x => 3.5 * travel(x) }
      ];
      if (mode === 'hz') return [{ name: 'Hz', color: c.colorLong, value: x => (1 - Math.cos(x * Math.PI * 8)) / 2 }];
      // Deliberately illustrative design curves. No measured force, contact time,
      // controller constants or proprietary ME program waveforms are encoded here.
      const pulse = (x, peak, center, spread) => peak * Math.exp(-.5 * ((x - center) / spread) ** 2) * Math.sin(Math.PI * x) ** 2;
      return [
        { name: 'Powder', color: c.colorPowder, value: x => pulse(x, .8, .49, .22) },
        { name: 'Satin', color: c.colorSatin, dash: '7 3', value: x => pulse(x, .57, .50, .29) },
        { name: 'Hairstroke', color: c.colorHairstroke, value: x => pulse(x, 1.05, .42, .105) },
        { name: 'Lips', color: c.colorLips, dash: '3 3', value: x => pulse(x, .77, .39, .14) }
      ];
    }
    drawPlot() {
      if (!this.plot) return;
      const mode = this.dataset.kind === 'stroke' ? 'stroke' : this.concept;
      const width = Math.max(240, this.plot.getBoundingClientRect().width);
      this.chart.querySelector('h3').textContent = mode === 'stroke' ? this.chart.dataset.titleStroke : mode === 'hz' ? this.chart.dataset.titleHz : this.chart.dataset.titleImpulse;
      const height = this.plot.getBoundingClientRect().height || 280;
      this.geometry = { left: 50, right: width - 18, top: 38, bottom: height - 44, max: mode === 'stroke' ? 3.7 : 1.1 };
      const g = this.geometry;
      this.series = this.chartSeries();
      this.plot.replaceChildren();
      this.plot.setAttribute('viewBox', `0 0 ${width} ${height}`);
      const node = (tag, attrs, label) => {
        const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
        Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
        if (label !== undefined) el.textContent = label;
        this.plot.append(el);
        return el;
      };
      const axis = mode === 'stroke' ? this.chart.dataset.yStroke : mode === 'hz' ? this.chart.dataset.yHz : this.chart.dataset.yImpulse;
      node('text', { x: g.left, y: 18, class: 'me-axis-title' }, axis);
      const ticks = mode === 'stroke' ? [0, 1, 2, 2.8, 3.5] : [0, .5, 1];
      ticks.forEach(value => {
        const y = this.chartY(value);
        node('line', { x1: g.left, x2: g.right, y1: y, y2: y, stroke: 'currentColor', opacity: .1 });
        if (mode === 'stroke') node('text', { x: g.left - 10, y: y + 4, 'text-anchor': 'end' }, value);
      });
      const divisions = width < 440 ? [0, .5, 1] : [0, .25, .5, .75, 1];
      divisions.forEach(x => node('text', { x: this.chartX(x), y: height - 25, 'text-anchor': x === 0 ? 'start' : x === 1 ? 'end' : 'middle' }, `${x * 100}%`));
      node('text', { x: (g.left + g.right) / 2, y: height - 4, 'text-anchor': 'middle', class: 'me-axis-title' }, mode === 'hz' ? this.chart.dataset.xHzLabel || this.chart.dataset.xLabel : this.chart.dataset.xLabel);
      this.chartDots = [];
      this.series.forEach(series => {
        const d = Array.from({ length: 161 }, (_, i) => `${i ? 'L' : 'M'}${this.chartX(i / 160).toFixed(2)} ${this.chartY(series.value(i / 160)).toFixed(2)}`).join(' ');
        node('path', { d, fill: 'none', stroke: series.color, 'stroke-width': 2.5, 'stroke-dasharray': series.dash || '' });
        this.chartDots.push(node('circle', { r: 4, fill: series.color }));
      });
      this.chartCursor = node('line', { y1: g.top, y2: g.bottom, stroke: 'currentColor', opacity: .2 });
      const legend = this.chart.querySelector('[data-chart-legend]');
      legend.replaceChildren();
      this.series.forEach(series => {
        const item = document.createElement('span'), swatch = document.createElement('i');
        swatch.style.setProperty('--series-color', series.color);
        if (series.dash) swatch.style.borderTopStyle = 'dashed';
        swatch.setAttribute('aria-hidden', 'true');
        item.append(swatch, document.createTextNode(series.name));
        legend.append(item);
      });
    }
    chartX(value) {
      return this.geometry.left + value * (this.geometry.right - this.geometry.left);
    }
    chartY(value) {
      return this.geometry.bottom - value / this.geometry.max * (this.geometry.bottom - this.geometry.top);
    }
    paint() {
      const displacement = (1 - Math.cos(this.phase * Math.PI * 2)) / 2;
      this.needles.forEach(needle => needle.setAttribute('transform', `translate(0 ${(displacement * Number(needle.dataset.distance)).toFixed(3)})`));
      this.sourceStages.forEach(stage => {
        const frame = Math.round(displacement * Number(stage.dataset.stroke) / 3.5 * 60);
        const image = stage.querySelector('[data-source-frame]');
        image.style.backgroundPosition = `${(frame % 8) * 100 / 7}% ${Math.floor(frame / 8) * 100 / 7}%`;
      });
      if (this.phaseInput) this.phaseInput.value = (this.phase * 100).toFixed(1);
      if (this.phaseOutput) this.phaseOutput.textContent = `${Math.round(this.phase * 100)}%`;
      if (this.plot && this.geometry) {
        const x = this.chartX(this.phase);
        this.chartDots.forEach((dot, index) => {
          dot.setAttribute('cx', x);
          dot.setAttribute('cy', this.chartY(this.series[index].value(this.phase)));
        });
        this.chartCursor.setAttribute('x1', x);
        this.chartCursor.setAttribute('x2', x);
      }
    }
    tick(time) {
      this.frame = 0;
      if (!this.isConnected || !this.playing || !this.visible || document.hidden) { this.last = null; return; }
      if (this.last !== null) {
        const speed = this.dataset.kind === 'stroke' ? this.hz / 60 : .25;
        this.phase = (this.phase + Math.min((time - this.last) / 1000, .08) * speed) % 1;
      }
      this.last = time;
      this.paint();
      this.frame = requestAnimationFrame(this.tick);
    }
    updatePlayback() {
      if (this.button) {
        this.button.textContent = this.playing ? this.button.dataset.pauseLabel : this.button.dataset.playLabel;
        this.button.setAttribute('aria-pressed', String(this.playing));
      }
      const run = this.playing && this.visible && !document.hidden;
      this.videos.forEach(video => {
        if (run && !video.closest('[hidden]')) {
          if (video.paused) video.play()?.catch(() => {});
        } else video.pause();
      });
      if (run && (this.needles.length || this.sourceStages.length || this.plot)) {
        if (!this.frame) { this.last = null; this.frame = requestAnimationFrame(this.tick); }
      } else {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.last = null;
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.colourRequest = (this.colourRequest || 0) + 1;
      cancelAnimationFrame(this.frame);
      this.videos?.forEach(video => video.pause());
      this.ready = false;
    }
  }
  customElements.define('me-learning', MELearning);
})();